import { smarticoRaffleSlice } from "./slice";
import { createAppSelector, useAppSelector } from "../../../hooks";
import { SmarticoRaffleDraw, TRaffleDrawRun } from "../interfaces/SmarticoRaffle";
import { SmarticoRaffleDrawIds } from "../interfaces/SmarticoRaffleDrawIds";
import {
  USE_MOCK_DATA,
  mockDrawsHistory,
  mockFutureDraws,
  mockPaginationByRunId,
  mockWinnersByRunId,
} from "./mockData";

export const useSmarticoRaffle = () => useAppSelector(state => state[smarticoRaffleSlice.name].raffle);

export const useSmarticoRaffleState = () => useAppSelector(state => state[smarticoRaffleSlice.name]);

const createSmarticoRaffleDrawByIdSelector = createAppSelector(
  [(state, drawId: number) => state[smarticoRaffleSlice.name], (state, drawId: number) => drawId],
  (raffleState, drawId) => {
    const draw = raffleState.raffle.draws?.find(draw => draw.id === drawId);
    if (draw) {
      return draw;
    }
    return {
      allow_multi_prize_per_ticket: false,
      background_image_url: "",
      background_image_url_mobile: "",
      current_state: undefined,
      description: "",
      execution_ts: 0,
      execution_type: undefined,
      icon_url: "",
      id: 0,
      image_url: "",
      image_url_mobile: "",
      is_grand: false,
      my_last_tickets: [],
      my_tickets_count: 0,
      name: "",
      previous_run_id: 0,
      previous_run_ts: 0,
      prizes: [],
      run_id: 0,
      ticket_start_ts: 0,
      total_tickets_count: 0,
    } as SmarticoRaffleDraw;
  }
);
export const useSmarticoRaffleDrawById = (drawId: number) =>
  useAppSelector(state => createSmarticoRaffleDrawByIdSelector(state, drawId));

interface UseSmarticoRaffleDrawsByIdOptions {
  showExpired?: boolean;
}

const createSmarticoRaffleDrawsByIdsSelector = createAppSelector(
  [
    (state, drawIds?: number[], _options?: UseSmarticoRaffleDrawsByIdOptions) => state[smarticoRaffleSlice.name],
    (state, drawIds?: number[], _options?: UseSmarticoRaffleDrawsByIdOptions) => drawIds,
    (state, drawIds?: number[], options?: UseSmarticoRaffleDrawsByIdOptions) => options,
  ],
  (raffleState, drawIds, options) => {
    let draws = raffleState.raffle.draws;
    if (drawIds) {
      draws = drawIds.reduce<SmarticoRaffleDraw[]>((newDraws, drawId) => {
        const draw = raffleState.raffle.draws.find(draw => draw.id === drawId);
        if (draw) {
          newDraws.push(draw);
        }
        return newDraws;
      }, []);
    }
    if (options?.showExpired === false) {
      draws = draws.filter(draw => Date.now() < draw.execution_ts);
    }
    return draws;
  }
);
export const useSmarticoRaffleDrawsById = (drawIds?: number[], options?: UseSmarticoRaffleDrawsByIdOptions) =>
  useAppSelector(state => createSmarticoRaffleDrawsByIdsSelector(state, drawIds, options));

const createIsQualifiedForDraw = createAppSelector(
  [(state, drawId: number) => state[smarticoRaffleSlice.name], (state, drawId: number) => drawId],
  (state, drawId) => {
    if (state.additionalData[drawId] === undefined) {
      return false;
    }
    const requiredTicketsForDraw = state.additionalData[drawId].requiredTickets;
    const draw = state.raffle.draws.find(draw => draw.id === drawId);
    if (draw) {
      return draw.my_tickets_count >= requiredTicketsForDraw;
    }
    return false;
  }
);
export const useIsQualifiedForDraw = (drawId: number) =>
  useAppSelector(state => createIsQualifiedForDraw(state, drawId));

const createSmarticoRaffleAdditionalDrawData = createAppSelector(
  [(state, drawId: number) => state[smarticoRaffleSlice.name], (state, drawId: number) => drawId],
  (state, drawId) =>
    state.additionalData[drawId] || {
      badge: "",
      requiredTickets: -1,
      title: "",
      description: "",
      image: "",
      displayOrder: undefined,
    }
);
export const useSmarticoRaffleAdditionalDrawData = (drawId: number) =>
  useAppSelector(state => createSmarticoRaffleAdditionalDrawData(state, drawId));

export const useSmarticoRaffleIsLoading = () =>
  useAppSelector(state => state[smarticoRaffleSlice.name].isRaffleLoading);

export const useDrawRunWinners = (run_id: number) =>
  useAppSelector(state => {
    if (USE_MOCK_DATA) {
      return mockWinnersByRunId[run_id] || [];
    }
    return state[smarticoRaffleSlice.name].drawRunWinners[run_id];
  });

export const useIsWinnersLoading = (run_id: number) =>
  useAppSelector(state => {
    if (USE_MOCK_DATA) {
      return false;
    }
    return state[smarticoRaffleSlice.name].loadingWinners[run_id] || false;
  });

const createRaffleDrawsHistorySelector = createAppSelector(
  [
    (state, ids?: number | number[]) => state[smarticoRaffleSlice.name].drawHistory,
    (state, ids?: number | number[]) => ids,
  ],
  (drawHistory, ids) => {
    if (Array.isArray(ids)) {
      return drawHistory.filter(item => ids.includes(item.id));
    }
    if (typeof ids === "number") {
      return drawHistory.filter(item => ids === item.id);
    }
    return drawHistory;
  }
);
export const useRaffleDrawsHistory = (ids?: number | number[]) =>
  useAppSelector(state => {
    if (USE_MOCK_DATA) {
      if (Array.isArray(ids)) {
        return mockDrawsHistory.filter(item => ids.includes(item.id));
      }
      if (typeof ids === "number") {
        return mockDrawsHistory.filter(item => ids === item.id);
      }
      return mockDrawsHistory;
    }
    return createRaffleDrawsHistorySelector(state, ids);
  });

export const useFutureRaffleDraws = () =>
  useAppSelector(state => {
    if (USE_MOCK_DATA) {
      return mockFutureDraws;
    }
    const endDate = state[smarticoRaffleSlice.name].raffle.end_date;
    const activeDailyDraw = state[smarticoRaffleSlice.name].raffle.draws.find(
      draw => draw.id === SmarticoRaffleDrawIds.HOURLY_DRAW
    );
    const diff = endDate - activeDailyDraw?.execution_ts;
    if (isNaN(diff)) {
      return [];
    }
    const diffInDays = Math.floor(diff / (1000 * 60 * 60 * 24));
    const diffBetweenStartAndEndDraw = activeDailyDraw.execution_ts - activeDailyDraw.ticket_start_ts;
    return Array<TRaffleDrawRun>(diffInDays)
      .fill({
        actual_execution_ts: 0,
        background_image_url: "",
        background_image_url_mobile: "",
        description: "",
        execution_ts: 0,
        has_unclaimed_prize: false,
        icon_url: "",
        id: 0,
        image_url: "",
        image_url_mobile: "",
        is_grand: false,
        is_winner: false,
        name: "",
        run_id: 0,
        ticket_start_ts: 0,
      })
      .reduce<TRaffleDrawRun[]>(
        (items, item, idx) => {
          if (idx === 0) {
            return items;
          }
          return [
            ...items,
            {
              ...item,
              ticket_start_ts: items[idx - 1].execution_ts,
              execution_ts: items[idx - 1].execution_ts + diffBetweenStartAndEndDraw,
            },
          ];
        },
        [
          {
            actual_execution_ts: 0,
            background_image_url: "",
            background_image_url_mobile: "",
            description: "",
            execution_ts: activeDailyDraw.execution_ts,
            has_unclaimed_prize: false,
            icon_url: "",
            id: 0,
            image_url: "",
            image_url_mobile: "",
            is_grand: false,
            is_winner: false,
            name: "",
            run_id: 0,
            ticket_start_ts: activeDailyDraw.ticket_start_ts,
          },
        ]
      );
  });

export const useRaffleDrawsHistoryLoading = () =>
  useAppSelector(state => {
    if (USE_MOCK_DATA) {
      return false;
    }
    return state[smarticoRaffleSlice.name].isDrawHistoryLoading;
  });

export const useRaffleRunPagination = (run_id: number) =>
  useAppSelector(state => {
    if (USE_MOCK_DATA && mockPaginationByRunId[run_id]) {
      return mockPaginationByRunId[run_id];
    }
    return (
      state[smarticoRaffleSlice.name].runPagination[run_id] || { winners_total: 0, winners_limit: 0, winners_offset: 0 }
    );
  });
